import { useEffect, useState } from 'react';

type Mood = 'happy' | 'neutral' | 'sad';
const MOODS: { value: Mood; emoji: string; label: string }[] = [
  { value: 'happy', emoji: '😊', label: 'Happy' },
  { value: 'neutral', emoji: '😐', label: 'Neutral' },
  { value: 'sad', emoji: '😞', label: 'Sad' },
];

/** Player feedback modal — POSTs to the shared /api/feedback collector (game: snake). */
export default function FeedbackForm({ onClose }: { onClose: () => void }) {
  const [mood, setMood] = useState<Mood | null>(null);
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const canSubmit = mood !== null && name.trim().length > 0 && message.trim().length > 0 && !submitting;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !mood) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mood, name: name.trim(), game: 'snake', message: message.trim() }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message || `Failed (${res.status})`);
      }
      setDone(true);
      setTimeout(onClose, 1200);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-[#1e3a5f]/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md rounded-3xl border border-white/60 bg-white p-6 shadow-[0_24px_60px_-16px_rgba(30,58,95,0.35)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Send feedback"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-wide text-snake-ink">FEEDBACK — SNAKE</h2>
          <button
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-full border border-snake-ink/10 bg-snake-ink/5 text-snake-ink/60 hover:bg-snake-ink/10 hover:text-snake-ink"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        {done ? (
          <p className="mt-4 rounded-2xl bg-emerald-50 px-4 py-6 text-center font-semibold text-emerald-700">
            Thanks for your feedback! 🐍
          </p>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <div className="flex gap-2">
              {MOODS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMood(m.value)}
                  className={`flex-1 rounded-2xl border px-2 py-3 text-sm font-bold transition ${
                    mood === m.value
                      ? 'border-[#4d7cff] bg-[#4d7cff]/10 text-snake-ink ring-2 ring-[#4d7cff]/20'
                      : 'border-snake-ink/10 bg-snake-ink/[0.04] text-snake-ink/60 hover:border-snake-ink/20'
                  }`}
                >
                  <span className="text-lg">{m.emoji}</span>
                  <span className="ml-1">{m.label}</span>
                </button>
              ))}
            </div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              maxLength={30}
              className="w-full rounded-xl border border-snake-ink/15 bg-white px-3 py-2.5 text-sm text-snake-ink placeholder:text-snake-ink/35 outline-none focus:border-[#4d7cff] focus:ring-2 focus:ring-[#4d7cff]/20"
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What should we fix or add?"
              rows={4}
              maxLength={500}
              className="w-full resize-none rounded-xl border border-snake-ink/15 bg-white px-3 py-2.5 text-sm text-snake-ink placeholder:text-snake-ink/35 outline-none focus:border-[#4d7cff] focus:ring-2 focus:ring-[#4d7cff]/20"
            />
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
            )}
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full rounded-2xl bg-[#4d7cff] py-3 text-sm font-bold tracking-widest text-white shadow-[0_4px_0_#3b66e0] transition hover:brightness-105 active:translate-y-0.5 active:shadow-none disabled:opacity-40"
            >
              {submitting ? 'SENDING…' : 'SEND FEEDBACK'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
